import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../services/api';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import {
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  UsersIcon,
  CurrencyRupeeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import FinanceLockGate from '../components/FinanceLockGate';

const currency = (value) => '₹' + Number(value || 0).toFixed(2);
const percent = (value) => Number(value || 0).toFixed(2) + '%';

function CardMetric({ label, value, subtext }) {
  return (
    <div className="metric-card">
      <div className="metric-label">{label}</div>
      <div className="metric-value text-[24px]">{value}</div>
      {subtext ? <p className="mt-2 text-sm text-slate-500">{subtext}</p> : null}
    </div>
  );
}

function ChartPanel({ title, subtitle, children }) {
  return (
    <div className="section-card p-5">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      <div className="h-[320px] rounded-[20px] bg-slate-50 p-4">
        {children}
      </div>
    </div>
  );
}

export default function BusinessReports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [intelligenceMode, setIntelligenceMode] = useState(false);
  const [reportData, setReportData] = useState({
    summary: {},
    charts: {
      revenueTrend: [],
      revenueVsCost: [],
      grossProfitTrend: [],
      grossMarginTrend: [],
      newPatientsTrend: [],
      retentionTrend: [],
      ltvTrend: [],
      chairUtilizationTrend: [],
      revenuePerChairTrend: [],
      cashRealizationTrend: [],
      emiExposureTrend: []
    }
  });

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/reports/summary');
      setReportData({
        summary: res.data && res.data.summary ? res.data.summary : {},
        charts: res.data && res.data.charts ? res.data.charts : {}
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load business reports');
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadPnL = async (period) => {
    try {
      const res = await axios.get('/reports/pdf?period=' + period, {
        responseType: 'blob'
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = period + '-pnl-report.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error('Failed to download P&L report');
    }
  };

  const summary = reportData.summary || {};
  const charts = reportData.charts || {};

  const headerText = useMemo(() => {
    if (!intelligenceMode) {
      return {
        subtitle:
          'A read-only business cockpit for revenue, margins, patient growth, utilization, and EMI risk.'
      };
    }

    return {
      subtitle:
        'Investor mode enabled: advanced unit economics, profitability ratios, cash realization, and EMI exposure indicators.'
    };
  }, [intelligenceMode]);

  return (
    <FinanceLockGate
      title="Finance Lock enabled. Enter PIN to continue."
      description="Business Reports contain revenue, cost, profit, tax, collections, and EMI exposure data."
      onAccessGranted={fetchReports}
    >
      {loading ? (
        <div className="section-card p-6">Loading reports...</div>
      ) : (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Business Reports</h1>
        <p className="page-subtitle">{headerText.subtitle}</p>
      </div>

      <div className="section-card p-5">
        <div className="mobile-stack-actions flex flex-wrap gap-3 sm:flex-row sm:items-center">
          <button onClick={() => downloadPnL('monthly')} className="btn-primary">
            <ArrowDownTrayIcon className="h-5 w-5" />
            Download Monthly P&amp;L
          </button>

          <button onClick={() => downloadPnL('annual')} className="btn-secondary">
            <ArrowDownTrayIcon className="h-5 w-5" />
            Download Annual P&amp;L
          </button>

          <button onClick={() => navigate('/setup')} className="btn-secondary">
            <Cog6ToothIcon className="h-5 w-5" />
            Go to Dektal Setup
          </button>

          <button onClick={() => navigate('/consultant-ledger')} className="btn-secondary">
            <UsersIcon className="h-5 w-5" />
            Consultant Fees Ledger
          </button>

          <button onClick={() => navigate('/monthly-reports')} className="btn-secondary">
            <ChartBarIcon className="h-5 w-5" />
            Monthly Cloud Reports
          </button>

          <button
            onClick={() => setIntelligenceMode((prev) => !prev)}
            className={intelligenceMode ? 'btn-primary' : 'btn-secondary'}
          >
            <PresentationChartLineIcon className="h-5 w-5" />
            Business Intelligence {intelligenceMode ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <CardMetric
          label="Monthly Revenue"
          value={currency(summary.monthlyRevenue)}
          subtext="Total billed amount for current month"
        />
        <CardMetric
          label="Net Profit"
          value={currency(summary.netProfit)}
          subtext={intelligenceMode ? 'EBIT after 25% tax' : 'Final profit after costs'}
        />
        <CardMetric
          label="Patient Retention"
          value={percent(summary.retention)}
          subtext="Returning patients within recent period"
        />
        <CardMetric
          label="EMI Exposure"
          value={percent(summary.emiExposure)}
          subtext="Outstanding EMI relative to current revenue"
        />
      </div>

      {intelligenceMode && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <CardMetric label="EBITDA Margin" value={percent(summary.ebitdaMargin)} />
          <CardMetric label="Net Profit Margin" value={percent(summary.netProfitMargin)} />
          <CardMetric label="Break-even Revenue" value={currency(summary.breakEvenRevenue)} />
          <CardMetric label="ROI" value={percent(summary.roi)} />
          <CardMetric label="Cash Realization" value={percent(summary.cashRealization)} />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartPanel
          title="Monthly Revenue Trend"
          subtitle="How revenue is changing over time."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts.revenueTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => currency(value)} />
              <Line type="monotone" dataKey="revenue" stroke="#4f46e5" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Revenue vs Treatment Cost"
          subtitle="Revenue compared against treatment-related costs."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.revenueVsCost || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => currency(value)} />
              <Legend />
              <Bar dataKey="revenue" fill="#4f46e5" radius={[8, 8, 0, 0]} />
              <Bar dataKey="cogs" fill="#f59e0b" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Gross Profit Trend"
          subtitle="Profit remaining after treatment costs."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts.grossProfitTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => currency(value)} />
              <Line type="monotone" dataKey="grossProfit" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Gross Margin Percentage"
          subtitle="Pricing and cost efficiency over time."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.grossMarginTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => percent(value)} />
              <Bar dataKey="grossMargin" fill="#10b981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="New Patient Trend"
          subtitle="New patients added per month."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.newPatientsTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="newPatients" fill="#3b82f6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Patient Retention Rate"
          subtitle="How many patients are returning for repeat treatment."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts.retentionTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => percent(value)} />
              <Line type="monotone" dataKey="retention" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Patient Lifetime Value"
          subtitle="Long-term patient value based on revenue and retention."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts.ltvTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => currency(value)} />
              <Line type="monotone" dataKey="ltv" stroke="#f97316" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Chair Utilization Rate"
          subtitle="Booked treatment minutes relative to available minutes."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.chairUtilizationTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => percent(value)} />
              <Bar dataKey="chairUtilization" fill="#06b6d4" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Revenue per Chair per Month"
          subtitle="Revenue generated by chair capacity."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts.revenuePerChairTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => currency(value)} />
              <Line type="monotone" dataKey="revenuePerChair" stroke="#0ea5e9" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Payment Collection Rate"
          subtitle="Amount collected compared against amount billed."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={charts.cashRealizationTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => percent(value)} />
              <Line type="monotone" dataKey="cashRealization" stroke="#22c55e" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="EMI Exposure Risk"
          subtitle="How much revenue is locked in outstanding EMI collections."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.emiExposureTrend || []}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => percent(value)} />
              <Bar dataKey="emiExposure" fill="#ef4444" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="metric-card">
          <div className="flex items-center gap-3">
            <ChartBarIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <div className="metric-label">Gross Profit</div>
              <div className="text-2xl font-bold text-slate-900">{currency(summary.grossProfit)}</div>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="flex items-center gap-3">
            <UsersIcon className="h-8 w-8 text-violet-600" />
            <div>
              <div className="metric-label">New Patients</div>
              <div className="text-2xl font-bold text-slate-900">{summary.newPatients || 0}</div>
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="flex items-center gap-3">
            <CurrencyRupeeIcon className="h-8 w-8 text-emerald-600" />
            <div>
              <div className="metric-label">LTV</div>
              <div className="text-2xl font-bold text-slate-900">{currency(summary.ltv)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
      )}
    </FinanceLockGate>
  );
}
